import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, FeatureGroup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { TextField, Box, Typography } from '@mui/material';
import { SearchIcon as Search } from 'lucide-react';
import api from '../axios/api';
import { showNotification } from '../redux/slices/notificationSlice';
import CustomButton from '../componentsII/Buttons';
import { useDispatch } from 'react-redux';
import * as L from "leaflet";

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

const SHAPE_COLORS = ['#FF0000', '#00FF00', '#0000FF', '#FFA500', '#800080'];

const MapViewUpdater = ({ center, zoom }) => {
    const map = useMap();
    useEffect(() => {
        if (center) {
            map.setView(center, zoom);
        }
    }, [center, zoom, map]);
    return null;
};

// const ExistingShapes = ({ geoFenceData, setDrawnItems, setTotalArea, map }) => {
//     const featureGroupRef = React.useRef();

//     useEffect(() => {
//         if (!geoFenceData) return;

//         try {
//             const parsedData = typeof geoFenceData === 'string'
//                 ? JSON.parse(geoFenceData)
//                 : geoFenceData;

//             // Initialize with existing drawn items instead of creating new Map
//             const featureGroup = featureGroupRef.current;
//             featureGroup.clearLayers();

//             if (parsedData?.features) {
//                 parsedData.features.forEach((feature, index) => {
//                     const color = feature.properties?.color || SHAPE_COLORS[index % SHAPE_COLORS.length];
//                     let layer;

//                     if (feature.geometry.type === 'Point' && feature.properties.radius) {
//                         const center = [
//                             feature.geometry.coordinates[1],
//                             feature.geometry.coordinates[0]
//                         ];
//                         layer = L.circle(center, {
//                             radius: feature.properties.radius,
//                             color: color
//                         });
//                         layer.addTo(featureGroup);

//                         // Add to drawnItems using setDrawnItems callback to preserve existing items
//                         setDrawnItems(prevItems => {
//                             const newItems = new Map(prevItems);
//                             newItems.set(layer._leaflet_id, {
//                                 type: 'circle',
//                                 center: center,
//                                 radius: feature.properties.radius,
//                                 color: color,
//                                 area: feature.properties.area || Math.PI * feature.properties.radius * feature.properties.radius
//                             });
//                             return newItems;
//                         });
//                     }
//                     else if (feature.geometry.type === 'Polygon') {
//                         const coordinates = feature.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);
//                         layer = L.polygon(coordinates, { color: color });
//                         layer.addTo(featureGroup);

//                         // Add to drawnItems using setDrawnItems callback to preserve existing items
//                         setDrawnItems(prevItems => {
//                             const newItems = new Map(prevItems);
//                             newItems.set(layer._leaflet_id, {
//                                 type: feature.properties.isRectangle ? 'rectangle' : 'polygon',
//                                 coordinates: coordinates,
//                                 color: color,
//                                 area: feature.properties.area || 0
//                             });
//                             return newItems;
//                         });
//                     }
//                 });

//                 // Update total area after all shapes are added
//                 setTimeout(() => {
//                     setDrawnItems(currentItems => {
//                         const totalArea = Array.from(currentItems.values())
//                             .reduce((sum, item) => sum + item.area, 0);
//                         setTotalArea(totalArea);
//                         return currentItems;
//                     });
//                 }, 0);

//                 // Fit map to the shapes
//                 if (featureGroup.getLayers().length > 0) {
//                     map?.fitBounds(featureGroup.getBounds());
//                 }
//             }
//         } catch (error) {
//             console.error('Error loading shapes:', error);
//         }
//     }, [geoFenceData]);

//     return <FeatureGroup ref={featureGroupRef} />;
// };

const ExistingShapes = ({ geoFenceData, setDrawnItems, setTotalArea, map, editFeatureGroupRef }) => {
    useEffect(() => {
        if (!geoFenceData || !editFeatureGroupRef.current) return;

        try {
            const parsedData = typeof geoFenceData === 'string'
                ? JSON.parse(geoFenceData)
                : geoFenceData;

            // Clear existing layers from the edit feature group
            editFeatureGroupRef.current.clearLayers();

            if (parsedData?.features) {
                parsedData.features.forEach((feature, index) => {
                    const color = feature.properties?.color || SHAPE_COLORS[index % SHAPE_COLORS.length];
                    let layer;

                    if (feature.geometry.type === 'Point' && feature.properties.radius) {
                        const center = [
                            feature.geometry.coordinates[1],
                            feature.geometry.coordinates[0]
                        ];
                        layer = L.circle(center, {
                            radius: feature.properties.radius,
                            color: color
                        });
                        // Add to the edit feature group instead of a separate group
                        layer.addTo(editFeatureGroupRef.current);

                        setDrawnItems(prevItems => {
                            const newItems = new Map(prevItems);
                            newItems.set(layer._leaflet_id, {
                                type: 'circle',
                                center: center,
                                radius: feature.properties.radius,
                                color: color,
                                area: feature.properties.area || Math.PI * feature.properties.radius * feature.properties.radius
                            });
                            return newItems;
                        });
                    }
                    else if (feature.geometry.type === 'Polygon') {
                        const coordinates = feature.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);
                        layer = L.polygon(coordinates, { color: color });
                        // Add to the edit feature group instead of a separate group
                        layer.addTo(editFeatureGroupRef.current);

                        setDrawnItems(prevItems => {
                            const newItems = new Map(prevItems);
                            newItems.set(layer._leaflet_id, {
                                type: feature.properties.isRectangle ? 'rectangle' : 'polygon',
                                coordinates: coordinates,
                                color: color,
                                area: feature.properties.area || 0
                            });
                            return newItems;
                        });
                    }
                });

                // Update total area after all shapes are added
                setTimeout(() => {
                    setDrawnItems(currentItems => {
                        const totalArea = Array.from(currentItems.values())
                            .reduce((sum, item) => sum + item.area, 0);
                        setTotalArea(totalArea);
                        return currentItems;
                    });
                }, 0);

                // Fit map to the shapes
                if (editFeatureGroupRef.current.getLayers().length > 0) {
                    map?.fitBounds(editFeatureGroupRef.current.getBounds());
                }
            }
        } catch (error) {
            console.error('Error loading shapes:', error);
        }
    }, [geoFenceData]);

    return null; // No need to return a FeatureGroup anymore
};

const GeoFencingModal = ({ ModalData, refetch, handleCloseMainDetailModal }) => {
    const dispatch = useDispatch();
    const [map, setMap] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [drawnItems, setDrawnItems] = useState(new Map());
    const [searchResults, setSearchResults] = useState([]);
    const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]);
    const [mapZoom, setMapZoom] = useState(5.5);
    const [totalArea, setTotalArea] = useState(ModalData?.totalArea || 0);
    const [currentColorIndex, setCurrentColorIndex] = useState(0);
    const [initialBounds, setInitialBounds] = useState(null);
    const editFeatureGroupRef = React.useRef();


    useEffect(() => {
        if (ModalData?.totalArea) {
            setTotalArea(ModalData.totalArea);
        }
    }, [ModalData?.totalArea]);

    useEffect(() => {
        if (ModalData?.geo_fence_latlong) {
            try {
                const parsedData = JSON.parse(ModalData.geo_fence_latlong);
                if (parsedData.features && parsedData.features.length > 0) {
                    const firstFeature = parsedData.features[0];
                    let center;
                    
                    if (firstFeature.geometry.type === 'Point') {
                        center = [
                            firstFeature.geometry.coordinates[1],
                            firstFeature.geometry.coordinates[0]
                        ];
                    } else if (firstFeature.geometry.type === 'Polygon') {
                        const coords = firstFeature.geometry.coordinates[0];
                        const lat = coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length;
                        const lng = coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length;
                        center = [lat, lng];
                    }
    
                    if (center) {
                        setMapCenter(center);
                        setMapZoom(13);
                        setSearchQuery(''); // Clear search query
                        setSearchResults([]); // Clear search results
                    }
                }
            } catch (error) {
                console.error('Error parsing geofence data:', error);
            }
        }
    }, [ModalData?.geo_fence_latlong]);


    useEffect(() => {
        if (ModalData?.city_name && (!ModalData?.geo_fence_latlong)) {
            setSearchQuery(ModalData.city_name);
            searchLocation(ModalData.city_name);
        }
    }, [ModalData?.city_name]);


    const updateMapView = (center, zoom = 13) => {
        setMapCenter(center);
        setMapZoom(zoom);
    };

    const convertToGeoJSON = () => {
        const features = Array.from(drawnItems.values()).map((item, index) => {
            const feature = {
                type: 'Feature',
                properties: {
                    color: item.color || SHAPE_COLORS[index % SHAPE_COLORS.length],
                    area: item.area
                }
            };

            if (item.type === 'circle') {
                feature.geometry = {
                    type: 'Point',
                    coordinates: [item.center[1], item.center[0]]
                };
                feature.properties.radius = item.radius;
            } else {
                feature.geometry = {
                    type: 'Polygon',
                    coordinates: [[...item.coordinates.map(coord => [coord[1], coord[0]])]]
                };
                if (item.type === 'rectangle') {
                    feature.properties.isRectangle = true;
                }
            }

            return feature;
        });

        return {
            type: 'FeatureCollection',
            features
        };
    };


    const searchLocation = async (query) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
            );
            const data = await response.json();
            if (data.length > 0) {
                setSearchResults(data);
                const { lat, lon } = data[0];
                setMapCenter([parseFloat(lat), parseFloat(lon)]);
                map?.setView([parseFloat(lat), parseFloat(lon)], 13);
            }
        } catch (error) {
            console.error('Error searching location:', error);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            searchLocation(searchQuery);
        }
    };

    const selectLocation = (location) => {
        const { lat, lon, display_name } = location;
        const newCenter = [parseFloat(lat), parseFloat(lon)];
        updateMapView(newCenter, 13);
        setSearchQuery(display_name);
        setSearchResults([]);
    };

    const calculateArea = (layer, type) => {
        if (!layer) return 0;

        try {
            switch (type) {
                case 'circle': {
                    const radius = layer.getRadius();
                    return Math.PI * radius * radius;
                }
                case 'polygon':
                case 'rectangle': {
                    const latLngs = layer.getLatLngs()[0];
                    const coordinates = latLngs.map(latLng => ({
                        lat: latLng.lat,
                        lng: latLng.lng
                    }));

                    const R = 6378137; // Earth's radius in meters
                    let area = 0;

                    for (let i = 0; i < coordinates.length - 1; i++) {
                        const p1 = coordinates[i];
                        const p2 = coordinates[i + 1];
                        area += p1.lat * p2.lng - p2.lat * p1.lng;
                    }

                    area += coordinates[coordinates.length - 1].lat * coordinates[0].lng -
                        coordinates[0].lat * coordinates[coordinates.length - 1].lng;

                    area = Math.abs(area * Math.PI * R * R / 360.0);
                    return area;
                }
                default:
                    return 0;
            }
        } catch (error) {
            console.error('Error calculating area:', error);
            return 0;
        }
    };

    const onCreated = (e) => {
        const layer = e.layer;
        const type = e.layerType;
        const color = SHAPE_COLORS[currentColorIndex % SHAPE_COLORS.length];

        layer.setStyle({ color });

        const area = calculateArea(layer, type);
        let shapeData = { color, area, type };

        if (type === 'circle') {
            const center = layer.getLatLng();
            shapeData = {
                ...shapeData,
                center: [center.lat, center.lng],
                radius: layer.getRadius()
            };
        } else {
            shapeData = {
                ...shapeData,
                coordinates: layer.getLatLngs()[0].map(latLng => [latLng.lat, latLng.lng])
            };
        }

        // Use callback form of setDrawnItems to ensure we preserve existing items
        setDrawnItems(prevItems => {
            const newItems = new Map(prevItems);
            newItems.set(layer._leaflet_id, shapeData);
            return newItems;
        });

        setCurrentColorIndex(prev => prev + 1);

        // Update total area using the callback form
        setDrawnItems(currentItems => {
            const newTotalArea = Array.from(currentItems.values())
                .reduce((sum, item) => sum + item.area, 0);
            setTotalArea(newTotalArea);
            return currentItems;
        });
    };

    // Modified onEdited function
    const onEdited = (e) => {
        setDrawnItems(prevItems => {
            const newItems = new Map(prevItems);
            let newTotalArea = 0;

            e.layers.eachLayer(layer => {
                if (newItems.has(layer._leaflet_id)) {
                    const currentItem = newItems.get(layer._leaflet_id);
                    const area = calculateArea(layer, currentItem.type);

                    let updatedData = { ...currentItem, area };

                    if (currentItem.type === 'circle') {
                        const center = layer.getLatLng();
                        updatedData = {
                            ...updatedData,
                            center: [center.lat, center.lng],
                            radius: layer.getRadius()
                        };
                    } else {
                        updatedData = {
                            ...updatedData,
                            coordinates: layer.getLatLngs()[0].map(latLng => [latLng.lat, latLng.lng])
                        };
                    }

                    newItems.set(layer._leaflet_id, updatedData);
                }
            });

            // Update total area
            newTotalArea = Array.from(newItems.values())
                .reduce((sum, item) => sum + item.area, 0);
            setTotalArea(newTotalArea);

            return newItems;
        });
    };

    // Modified onDeleted function
    const onDeleted = (e) => {
        setDrawnItems(prevItems => {
            const newItems = new Map(prevItems);

            e.layers.eachLayer(layer => {
                newItems.delete(layer._leaflet_id);
            });

            const newTotalArea = Array.from(newItems.values())
                .reduce((sum, item) => sum + item.area, 0);
            setTotalArea(newTotalArea);

            return newItems;
        });
    };

    const handleSave = async () => {
        try {
            if (drawnItems.size === 0) {
                dispatch(showNotification({
                    message: 'Please draw a geofence area first',
                    type: 'error'
                }));
                return;
            }

            const geofenceData = convertToGeoJSON();

            await api.post('/company/AddUpdateCompanyLocationGeofence', {
                id: ModalData?.id,
                geofenceData: JSON.stringify(geofenceData),
                totalArea: totalArea
            });

            dispatch(showNotification({
                message: 'Geofence data saved successfully',
                type: 'success'
            }));

            refetch?.();
            handleCloseMainDetailModal?.();
        } catch (error) {
            console.error('Error saving geofence data:', error);
            dispatch(showNotification({
                message: 'Error saving geofence data',
                type: 'error'
            }));
        }
    };


    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Add Geofencing</Typography>
            </Box>
            <Box sx={{ width: '1000px', overflow: 'auto', height: '500px', p: 2 }}>
                <Box sx={{ mb: 2 }}>
                    <form onSubmit={handleSearch} style={{ display: 'flex', gap: '16px' }}>
                        <TextField
                            fullWidth
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search location..."
                            size="small"
                            variant="outlined"
                        />
                        <CustomButton
                            type="submit"
                            text={<Search />}
                            className="bg-green-600 hover:bg-green-700 text-white"
                        />
                    </form>

                    {searchResults.length > 0 && (
                        <Box sx={{ mt: 2, maxHeight: '200px', overflow: 'auto' }}>
                            {searchResults.map((result, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        p: 1,
                                        cursor: 'pointer',
                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
                                    }}
                                    onClick={() => selectLocation(result)}
                                >
                                    <Typography variant="body2">{result.display_name}</Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                <Box sx={{ height: '500px', position: 'relative' }}>
                <MapContainer
                    center={mapCenter}
                    zoom={mapZoom}
                    style={{ height: '100%' }}
                    whenCreated={setMap}
                >
                    <MapViewUpdater center={mapCenter} zoom={mapZoom} />
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {/* Single FeatureGroup for all shapes */}
                    <FeatureGroup ref={editFeatureGroupRef}>
                        <EditControl
                            position="topright"
                            onCreated={onCreated}
                            onEdited={onEdited}
                            onDeleted={onDeleted}
                            draw={{
                                rectangle: true,
                                circle: true,
                                polygon: true,
                                circlemarker: false,
                                marker: false,
                                polyline: false,
                            }}
                        />
                        {ModalData?.geo_fence_latlong && (
                            <ExistingShapes
                                geoFenceData={ModalData.geo_fence_latlong}
                                setDrawnItems={setDrawnItems}
                                setTotalArea={setTotalArea}
                                map={map}
                                editFeatureGroupRef={editFeatureGroupRef}
                            />
                        )}
                    </FeatureGroup>
                </MapContainer>
            </Box>
            </Box>
            <Box >

                <Typography variant="body2" color="text.secondary" sx={{mt:1,mx:1,fontSize:"1.5rem",backgroundColor:"#fffca1",borderRadius:"10px",padding:"10px",color:"black",p:1,width:"fit-content",border:"1px solid black",":hover":{cursor:"pointer",backgroundColor:"#fcf865",color:"black"}}}>
                 {totalArea===0 ? "No Data Provided Yet" : `Total Area:  ${totalArea} m²`}
                </Typography>
            </Box>
            <div className="w-full flex justify-end gap-5 items-center py-5">
                <CustomButton
                    className="bg-gray-500 hover:bg-gray-700 text-white"
                    click={handleCloseMainDetailModal}
                    text="Cancel"
                />
                <CustomButton
                    className="bg-green-600 hover:bg-green-700 text-white"
                    text="Submit"
                    click={handleSave}
                />
            </div>
        </>
    );
};

export default GeoFencingModal;